import React, { useState } from 'react';
import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { LegendariesTable, Legendaries } from "@dndModels";
import { CHARACTER_LEVELS, CREATURE_CRS, SIMPLE_CR_OPTIONS } from "@dndModels/Constants";

import { makeStyles } from "@utilities/theme_utilities";

import AddButton from "@components/input/buttons/AddButton";
import StringBox from "@components/input/strings/StringBox";
import DeleteButton from "@components/input/buttons/DeleteButton";
import CheckBox from "@components/input/CheckBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import ButtonBox from "@components/input/buttons/ButtonBox";

interface Props { 
  obj: LegendariesTable;
  disabled: boolean;
  creature?: boolean;
  fields?: string[];
  onChange: (changed: LegendariesTable) => void;
}

export default function LegendariesTableInput({
  obj,
  disabled,
  creature = false,
  fields,
  onChange,
}: Props) {
  const [add_at_level, setAddAtLevel] = useState("Next");
  const [shift_by_level, setShiftByLevel] = useState(0);
  const [trickle_up, setTrickleUp] = useState(false);
  const [ping, setPing] = useState(false);
  const [view, setView] = useState("Main");
  const [parse_string, setParseString] = useState("");
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        color: "white"
      },
    });
  });

  const onAdd = () => {
    // debugger;
    const new_obj = obj;
    const entry = new Legendaries();
    if (add_at_level === "Next") {
      const max_level = Math.max(...new_obj.entries.map(o => o.level), 0);
      entry.level = 1;
      const previous = new_obj.entries.find(o => o.level === max_level);
      if (previous) {
        entry.copy(previous);
        if (previous.level > 0.5) { // It's going to be a whole number, so just increment
          entry.level = previous.level + 1;
        } else {
          const level_options = (creature ? [...SIMPLE_CR_OPTIONS] : [...CHARACTER_LEVELS]).map(o => +o).filter(o => o > previous.level);
          entry.level = Math.min(...level_options);
        }
      }
    } else {
      entry.level = +add_at_level;
      const max_level = Math.max(...new_obj.entries.filter(o => o.level < entry.level).map(o => o.level), 0);
      const previous = new_obj.entries.find(o => o.level === max_level);
      if (previous) {
        entry.copy(previous);
      } else {
        // It's a new lowest.  Make it match the previous lowest, and remove trickle up.
        const min_level = new_obj.entries.length === 0 ? 0 : Math.min(...new_obj.entries.map(o => o.level));
        const lowest = new_obj.entries.find(o => o.level === min_level);
        if (lowest) {
          entry.copy(lowest);
          setTrickleUp(false);
        }
      }
      setAddAtLevel("Next");
    }
    if (!isNaN(entry.level)) {
      new_obj.entries.push(entry);
      onChange(new_obj);
      setPing(!ping);
    } else {
      console.error("Logic Error.  Should never go here.");
    }
  }

  const parse = () => {
    const new_obj = obj;
    new_obj.entries = [];
    const parsed = JSON.parse(parse_string);
    if (typeof parsed === "object" && parsed.entries && parsed.entries.length > 0) {
      for (const entry of parsed.entries) {
        new_obj.entries.push(new Legendaries(entry));
      }
      if (typeof parsed.res_desc === "string") {
        new_obj.res_desc = parsed.res_desc;
      }
      if (typeof parsed.act_desc === "string") {
        new_obj.act_desc = parsed.act_desc;
      }
    }
    onChange(new_obj);
    setPing(!ping);
  }
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  // const height = window.innerHeight - 64;
  
  let level_options = creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS];
  level_options = level_options.filter(o => obj.entries.filter(o2 => `${o2.level}` === o).length === 0);
  const entry_levels = obj.entries.map(o => o.level);
  const min = entry_levels.length === 0 ? 0 : Math.min(...entry_levels);
  const max = entry_levels.length === 0 ? 0 : Math.max(...entry_levels);
  const empty_entries = level_options.map(o => +o).filter(o => o > min && o < max);
  level_options.push("Next");
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <CenteredMenu
          options={["Main", "Parse"]}
          selected={view}
          onSelect={setView}
        />
      </Grid>
      { view === "Main" ?
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Actions Description"
              value={obj.act_desc}
              disabled={disabled}
              multiline
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.act_desc = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Resistances Description"
              value={obj.res_desc}
              disabled={disabled}
              multiline
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.res_desc = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            className="header"
            size={{
              xs: 4
            }}>
            Entries&nbsp;
            <AddButton
              disabled={disabled}
              onClick={onAdd}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox
              name="Add at Level"
              options={level_options}
              value={add_at_level}
              disabled={disabled}
              onChange={setAddAtLevel}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <CheckBox
              name="Trickle Up"
              value={trickle_up}
              disabled={disabled}
              onChange={setTrickleUp}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox
              name="Shift by Level"
              value={`${shift_by_level}`}
              disabled={disabled}
              onBlur={(changed: string) => {
                setShiftByLevel(+changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <ButtonBox
              name="Shift Up"
              disabled={disabled || shift_by_level === 0}
              onClick={() => {
                const new_obj = obj;
                for (const entry of new_obj.entries) {
                  entry.level += shift_by_level;
                }
                onChange(new_obj);
                setShiftByLevel(0);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <ButtonBox
              name="Shift Down"
              disabled={disabled || shift_by_level === 0}
              onClick={() => {
                const new_obj = obj;
                for (const entry of new_obj.entries) {
                  entry.level -= shift_by_level;
                }
                onChange(new_obj);
                setShiftByLevel(0);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <ButtonBox
              name="Extrapolate Empty Entries"
              disabled={disabled || empty_entries.length === 0}
              onClick={() => {
                const new_obj = obj;
                new_obj.extrapolateEmptyFields();
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          { obj.entries.sort((a, b) => {
            return a.level - b.level; 
          }).map(entry => {
            return (
              <Grid size={12} key={entry.true_id} className="padded">
                <Grid container spacing={0} direction="row"
                  className={cx2(rowClasses.root)}>
                  <Grid size={2}>
                    <SelectStringBox 
                      value={`${entry.level}`} 
                      name="Level"
                      disabled={disabled}
                      options={creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS]}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        entry.level = +changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <SelectStringBox 
                      value={`${entry.actions}`} 
                      name="Actions"
                      disabled={disabled}
                      options={["0","1","2","3","4","5","6","7","8","9","10"]}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        entry.actions = +changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <SelectStringBox 
                      value={`${entry.resistances}`} 
                      name="Resistances"
                      disabled={disabled}
                      options={["0","1","2","3","4","5","6","7","8","9","10"]}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        entry.resistances = +changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <SelectStringBox 
                      value={`${entry.act_lair}`} 
                      name="Actions in Lair"
                      disabled={disabled}
                      options={["0","1","2","3","4","5","6","7","8","9","10"]}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        entry.act_lair = +changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <SelectStringBox 
                      value={`${entry.res_lair}`} 
                      name="Resistances in Lair"
                      disabled={disabled}
                      options={["0","1","2","3","4","5","6","7","8","9","10"]}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        entry.res_lair = +changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <DeleteButton
                      onClick={() => {
                        const new_obj = obj;
                        new_obj.entries = new_obj.entries.filter(o => o.level !== entry.level);
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <StringBox 
                      name="Lair Actions Extra"
                      value={`${entry.lair_actions_extra}`} 
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        debugger;
                        const new_obj = obj;
                        entry.lair_actions_extra = changed;
                        onChange(new_obj);
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </>
      :
        <>
          <Grid
            size={{
              xs: 9
            }}>
            <StringBox 
              name="Parser"
              value={parse_string}
              multiline
              onBlur={(changed: string) => {
                while (changed.includes("–")) {
                  changed = changed.replace("–","-");
                }
                setParseString(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button variant="contained" color="primary" disabled={disabled}
              onClick={parse}>
              Parse
            </Button>
          </Grid>
        </>
      }
    </Grid>
  );
}

